import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import AdminLayout from "@/components/admin/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { useToast } from "@/hooks/use-toast";
import { Brain, CheckCircle, XCircle, Loader2, AlertCircle } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface GenerationResult {
  postId: string;
  title: string;
  questionsGenerated?: number;
  status: 'success' | 'failed';
  error?: string;
}

const BulkQuizGenerator = () => {
  const { toast } = useToast();
  const [stats, setStats] = useState({
    totalPosts: 0,
    postsWithQuizzes: 0,
    postsNeedingQuizzes: 0,
  });
  const [isGenerating, setIsGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [results, setResults] = useState<GenerationResult[]>([]);
  const [minQuestions, setMinQuestions] = useState(5);
  const [maxQuestions, setMaxQuestions] = useState(10);
  const [delayBetweenRequests, setDelayBetweenRequests] = useState(3000);
  const [generationComplete, setGenerationComplete] = useState(false);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const { count: totalPosts } = await supabase
        .from("blog_posts")
        .select("*", { count: "exact", head: true })
        .eq("status", "published");

      const { count: postsWithQuizzes } = await supabase
        .from("quizzes")
        .select("*", { count: "exact", head: true });

      setStats({
        totalPosts: totalPosts || 0,
        postsWithQuizzes: postsWithQuizzes || 0,
        postsNeedingQuizzes: (totalPosts || 0) - (postsWithQuizzes || 0),
      });
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  const handleBulkGenerate = async () => {
    if (stats.postsNeedingQuizzes === 0) {
      toast({
        title: "No posts need quizzes",
        description: "All published posts already have quizzes!",
      });
      return;
    }

    setIsGenerating(true);
    setProgress(0);
    setResults([]);
    setGenerationComplete(false);

    try {
      const { data, error } = await supabase.functions.invoke('bulk-generate-quizzes', {
        body: { minQuestions, maxQuestions, delayBetweenRequests }
      });

      if (error) throw error;

      if (data.error) {
        if (data.error.includes('credits exhausted') || data.error.includes('402')) {
          toast({
            variant: "destructive",
            title: "Credits Exhausted",
            description: "Lovable AI credits have been exhausted. Please add credits to continue.",
          });
        } else if (data.error.includes('Rate limit') || data.error.includes('429')) {
          toast({
            variant: "destructive",
            title: "Rate Limit Exceeded",
            description: "Too many requests. Please try again in a few minutes.",
          });
        } else {
          throw new Error(data.error);
        }
        setIsGenerating(false);
        return;
      }

      setResults(data.progress || []);
      setProgress(100);
      setGenerationComplete(true);

      toast({
        title: "Bulk Generation Complete!",
        description: `Successfully generated ${data.successful} quizzes. ${data.failed} failed.`,
      });

      // Refresh stats
      await fetchStats();

    } catch (error) {
      console.error("Error during bulk generation:", error);
      toast({
        variant: "destructive",
        title: "Generation Failed",
        description: error instanceof Error ? error.message : "An unexpected error occurred",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const successfulResults = results.filter(r => r.status === 'success');
  const failedResults = results.filter(r => r.status === 'failed');

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Brain className="h-8 w-8" />
            Bulk Quiz Generator
          </h1>
          <p className="text-muted-foreground mt-2">
            Automatically generate AI-powered quizzes for all blog posts
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid gap-4 md:grid-cols-3">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-medium">Total Published Posts</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{stats.totalPosts}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-medium">Posts with Quizzes</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-green-600">{stats.postsWithQuizzes}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-medium">Posts Needing Quizzes</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-orange-600">{stats.postsNeedingQuizzes}</div>
            </CardContent>
          </Card>
        </div>

        {/* Generation Controls */}
        <Card>
          <CardHeader>
            <CardTitle>Generation Settings</CardTitle>
            <CardDescription>Configure quiz generation parameters</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 md:grid-cols-3">
              <div className="space-y-2">
                <Label htmlFor="minQuestions">Min Questions per Quiz</Label>
                <Input
                  id="minQuestions"
                  type="number"
                  min="3"
                  max="15"
                  value={minQuestions}
                  onChange={(e) => setMinQuestions(parseInt(e.target.value))}
                  disabled={isGenerating}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="maxQuestions">Max Questions per Quiz</Label>
                <Input
                  id="maxQuestions"
                  type="number"
                  min="3"
                  max="15"
                  value={maxQuestions}
                  onChange={(e) => setMaxQuestions(parseInt(e.target.value))}
                  disabled={isGenerating}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="delayBetweenRequests">Delay Between Posts (seconds)</Label>
                <Input
                  id="delayBetweenRequests"
                  type="number"
                  min="1"
                  max="30"
                  value={delayBetweenRequests / 1000}
                  onChange={(e) => setDelayBetweenRequests(parseInt(e.target.value) * 1000)}
                  disabled={isGenerating}
                />
                <p className="text-sm text-muted-foreground">
                  Time to wait between processing each post to avoid rate limits. Recommended: 3-5 seconds.
                </p>
              </div>
            </div>

            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                This will generate {minQuestions}-{maxQuestions} questions for each of the {stats.postsNeedingQuizzes} posts.
                Estimated time: {Math.ceil((stats.postsNeedingQuizzes * 5) / 60)} minutes.
              </AlertDescription>
            </Alert>

            <Button
              onClick={handleBulkGenerate}
              disabled={isGenerating || stats.postsNeedingQuizzes === 0}
              className="w-full"
              size="lg"
            >
              {isGenerating ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Generating Quizzes...
                </>
              ) : (
                <>
                  <Brain className="mr-2 h-4 w-4" />
                  Generate All Missing Quizzes ({stats.postsNeedingQuizzes} posts)
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Progress Section */}
        {(isGenerating || generationComplete) && (
          <Card>
            <CardHeader>
              <CardTitle>Generation Progress</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Overall Progress</span>
                  <span>{progress}%</span>
                </div>
                <Progress value={progress} />
              </div>

              {generationComplete && (
                <div className="grid gap-2 md:grid-cols-2">
                  <div className="flex items-center gap-2 p-3 bg-green-50 dark:bg-green-950 rounded-lg">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    <div>
                      <div className="font-medium">{successfulResults.length} Successful</div>
                      <div className="text-sm text-muted-foreground">Quizzes created</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-950 rounded-lg">
                    <XCircle className="h-5 w-5 text-red-600" />
                    <div>
                      <div className="font-medium">{failedResults.length} Failed</div>
                      <div className="text-sm text-muted-foreground">Errors occurred</div>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Results Section */}
        {results.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Generation Results</CardTitle>
              <CardDescription>Detailed results for each blog post</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {results.map((result, index) => (
                  <div
                    key={index}
                    className={`flex items-center justify-between p-3 rounded-lg ${
                      result.status === 'success'
                        ? 'bg-green-50 dark:bg-green-950'
                        : 'bg-red-50 dark:bg-red-950'
                    }`}
                  >
                    <div className="flex items-center gap-3 flex-1">
                      {result.status === 'success' ? (
                        <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
                      ) : (
                        <XCircle className="h-5 w-5 text-red-600 flex-shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate">{result.title}</div>
                        {result.status === 'failed' && result.error && (
                          <div className="text-sm text-red-600 dark:text-red-400 truncate">
                            {result.error}
                          </div>
                        )}
                      </div>
                    </div>
                    {result.status === 'success' && result.questionsGenerated && (
                      <Badge variant="secondary">
                        {result.questionsGenerated} questions
                      </Badge>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </AdminLayout>
  );
};

export default BulkQuizGenerator;
